import {
  Flex,
  useDisclosure,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Icon,
  HStack,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Grid,
  GridItem,
  Divider,
  VStack,
  Tag,
  TagLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanSelector } from '../helivan/HelivanSelector';
import { CreateAudienceModal } from './CreateAudienceModal';
import { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import {
  FaChevronDown,
  FaLightbulb,
  FaChartLine,
  FaChartPie,
  FaCommentDots,
  FaThumbsUp,
  FaThumbsDown,
} from 'react-icons/fa';

interface AudienceDataPoint {
  x: number;
  y: number;
  username: string;
  followers: number;
  engagement: number;
  engagementWithYou: number;
  engagementOverall: number;
  topics: string[];
  needsAttention: boolean;
}

// Available topics
const TOPICS = [
  'overall',
  'comedy',
  'fashion',
  'fitness',
  'beauty',
  'lifestyle',
];

// Generate random Twitter usernames
const generateRandomUsername = () => {
  const prefixes = [
    'user',
    'twitter',
    'social',
    'tech',
    'dev',
    'design',
    'creative',
    'digital',
    'web',
    'data',
  ];
  const suffixes = [
    'fan',
    'lover',
    'guru',
    'expert',
    'enthusiast',
    'pro',
    'master',
    'geek',
    'nerd',
    'wizard',
  ];
  const randomNum = Math.floor(Math.random() * 10000);

  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];

  return `@${prefix}_${suffix}${randomNum}`;
};

// Assign random topics to a data point
const assignRandomTopics = () => {
  const topics = [];
  // Skip 'overall' as it's not a real topic
  const availableTopics = TOPICS.slice(1);

  // Assign 1-3 random topics
  const numTopics = Math.floor(Math.random() * 3) + 1;
  const shuffledTopics = [...availableTopics].sort(() => 0.5 - Math.random());

  for (let i = 0; i < numTopics; i++) {
    topics.push(shuffledTopics[i]);
  }

  return topics;
};

// Generate random data points
const generateRandomData = (count = 200): AudienceDataPoint[] => {
  const data: AudienceDataPoint[] = [];
  for (let i = 0; i < count; i++) {
    // Generate engagement rates
    const engagementOverall = Math.random() * 10;
    // For some users, make engagement with YOU lower than overall
    const engagementWithYou =
      Math.random() > 0.4
        ? engagementOverall + Math.random() * 2
        : engagementOverall - Math.random() * 3;

    const needsAttention = engagementWithYou < engagementOverall;

    data.push({
      x: Math.random() * 100 - 50, // Center around 0
      y: Math.random() * 100 - 50, // Center around 0
      username: generateRandomUsername(),
      followers: Math.floor(Math.random() * 100000),
      engagement: engagementOverall,
      engagementWithYou: Math.max(0, engagementWithYou), // Ensure it's not negative
      engagementOverall,
      topics: assignRandomTopics(),
      needsAttention,
    });
  }
  return data;
};

// Sample trending topics
const TRENDING_TOPICS = [
  'AI technology',
  'sustainable fashion',
  'mental health',
  'home workouts',
  'plant-based diet',
  'skincare routines',
  'productivity hacks',
  'travel plans',
];

// Sample forgotten topics
const FORGOTTEN_TOPICS = [
  'NFTs',
  'pandemic hobbies',
  'sourdough bread',
  'TikTok dances',
  'home office setup',
  'virtual events',
  'cryptocurrency',
  'zoom backgrounds',
];

// Sample post data
const SAMPLE_POSTS = [
  {
    id: 1,
    title: 'Morning Skincare Routine',
    engagement: 8.7,
    reason:
      'This post performed well because it combined practical tips with personal experience. The step-by-step format was easy to follow, and you included product recommendations across different price points.',
  },
  {
    id: 2,
    title: 'My Fitness Journey',
    engagement: 9.2,
    reason:
      'Your authentic storytelling resonated with followers. The before/after photos and specific challenges you overcame created an emotional connection. The actionable workout tips at the end gave it practical value.',
  },
  {
    id: 3,
    title: 'Tech Gadget Review',
    engagement: 3.1,
    reason:
      'This post underperformed because it lacked your personal perspective and felt too promotional. The content was also too technical for your core audience who primarily follow you for lifestyle and wellness content.',
  },
  {
    id: 4,
    title: 'Weekend Travel Vlog',
    engagement: 2.8,
    reason:
      "The post was too long and lacked a clear focus. The destination wasn't relevant to most followers' interests, and the posting time (late Sunday) missed your peak engagement window.",
  },
];

// Content suggestions based on audience data
const CONTENT_SUGGESTIONS = [
  "Create a 'day in the life' video focusing on your morning fitness routine",
  'Share your top 5 sustainable fashion brands with outfit ideas',
  'Collaborate with a comedy creator to reach new audiences',
  'Post a Q&A addressing beauty myths your followers believe',
  'Create a series on mental wellness practices that complement your fitness content',
];

export const Audience = () => {
  const audienceModal = useDisclosure();
  const insightsModal = useDisclosure();
  const [audienceData, setAudienceData] = useState<AudienceDataPoint[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('overall');
  const [filteredData, setFilteredData] = useState<AudienceDataPoint[]>([]);
  const [tabIndex, setTabIndex] = useState<number>(0);

  useEffect(() => {
    const data = generateRandomData(200);
    setAudienceData(data);
    setFilteredData(data);
  }, []);

  useEffect(() => {
    if (selectedTopic === 'overall') {
      setFilteredData(audienceData);
    } else {
      const filtered = audienceData.filter((d) =>
        d.topics.includes(selectedTopic)
      );
      setFilteredData(filtered);
    }
  }, [selectedTopic, audienceData]);

  const handleTakeAction = (action: string) => {
    console.log(`Action selected: ${action}`);
    // Implement action handling logic here
  };

  const topicOptions = TOPICS.map((topic) => ({
    key: topic,
    value: topic.charAt(0).toUpperCase() + topic.slice(1), // Capitalize first letter
  }));

  // Count how many users need attention in each topic
  const attentionByTopic = TOPICS.slice(1)
    .map((topic) => {
      const usersInTopic = audienceData.filter((d) => d.topics.includes(topic));
      const needAttention = usersInTopic.filter((d) => d.needsAttention).length;
      return { topic, count: needAttention, total: usersInTopic.length };
    })
    .sort((a, b) => b.count / b.total - a.count / a.total);

  // Get the topic with the highest percentage of users needing attention
  // const topAttentionTopic =
  //   attentionByTopic.length > 0 ? attentionByTopic[0].topic : '';

  // Count how many users need attention in the current filtered view
  const needsAttentionCount = filteredData.filter(
    (d) => d.needsAttention
  ).length;
  const attentionPercentage =
    filteredData.length > 0
      ? Math.round((needsAttentionCount / filteredData.length) * 100)
      : 0;

  // Get engagement stats for visualization
  const topicEngagementStats = TOPICS.slice(1)
    .map((topic) => {
      const usersInTopic = audienceData.filter((d) => d.topics.includes(topic));
      if (usersInTopic.length === 0) return { topic, avgEngagement: 0 };

      const avgEngagement =
        usersInTopic.reduce((sum, user) => sum + user.engagementWithYou, 0) /
        usersInTopic.length;

      return { topic, avgEngagement };
    })
    .sort((a, b) => b.avgEngagement - a.avgEngagement);

  // Get the topics with highest and lowest average engagement
  const topEngagementTopic =
    topicEngagementStats.length > 0 ? topicEngagementStats[0].topic : '';
  const lowEngagementTopic =
    topicEngagementStats.length > 0
      ? topicEngagementStats[topicEngagementStats.length - 1].topic
      : '';

  // Get best and worst performing posts
  const bestPost = SAMPLE_POSTS.reduce((prev, current) =>
    prev.engagement > current.engagement ? prev : current
  );

  const worstPost = SAMPLE_POSTS.reduce((prev, current) =>
    prev.engagement < current.engagement ? prev : current
  );

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  // Dashboard Tab Content
  const DashboardContent = () => (
    <Flex direction="column" height="100%" overflow="hidden">
      <Stack spacing={4} p={4} flexShrink={0}>
        <Flex width={'full'} justifyContent={'space-between'}>
          <Box width="200px">
            <HelivanSelector
              options={topicOptions}
              selectedOption={selectedTopic}
              onSelect={setSelectedTopic}
              size="sm"
            />
          </Box>

          <HStack width={'full'} justifyContent={'flex-end'} spacing={4}>
            <HelivanButton
              leftIcon={<FaLightbulb />}
              variant="outline"
              onClick={insightsModal.onOpen}
            >
              Insights
            </HelivanButton>

            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<Icon as={FaChevronDown} />}
                bg={HelivanColor.background.secondary}
                color={HelivanColor.text.black}
                borderColor={HelivanColor.border.primary}
                _hover={{ bg: HelivanColor.hover.primary }}
                _active={{ bg: HelivanColor.hover.primary }}
              >
                Take Action
              </MenuButton>
              <MenuList
                bg={HelivanColor.background.secondary}
                borderColor={HelivanColor.border.primary}
              >
                <MenuItem
                  onClick={() => handleTakeAction('create-explorable')}
                  _hover={{ bg: HelivanColor.hover.primary }}
                >
                  Create Project
                </MenuItem>
                <MenuItem
                  onClick={() => handleTakeAction('poll-audience')}
                  _hover={{ bg: HelivanColor.hover.primary }}
                >
                  Poll Audience
                </MenuItem>
                <MenuItem
                  onClick={() => handleTakeAction('test-social-post')}
                  _hover={{ bg: HelivanColor.hover.primary }}
                >
                  Test Social Post
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>
      </Stack>

      <Box flex="1" overflow="auto" p={4}>
        <Plot
          data={[
            // Regular audience dots
            {
              x: filteredData.map((d) => d.x),
              y: filteredData.map((d) => d.y),
              mode: 'markers',
              type: 'scatter',
              marker: {
                size: 10,
                color: filteredData.map((d) => d.engagement),
                colorscale: 'Viridis',
                showscale: true,
                colorbar: { title: { text: 'Engagement Rate' } },
                line: {
                  width: filteredData.map((d) => (d.needsAttention ? 2 : 0)),
                  color: 'red',
                },
              },
              text: filteredData.map(
                (d) =>
                  `${d.username}<br>` +
                  `Followers: ${d.followers.toLocaleString()}<br>` +
                  `Engagement Overall: ${d.engagementOverall.toFixed(2)}<br>` +
                  `Engagement with YOU: ${d.engagementWithYou.toFixed(2)}<br>` +
                  `Topics: ${d.topics.join(', ')}<br>` +
                  `${d.needsAttention ? '⚠️ Needs attention' : '✅ Good engagement'}`
              ),
              hoverinfo: 'text',
            },
            // YOU dot in the center
            {
              x: [0],
              y: [0],
              mode: 'markers+text' as any, // Type assertion to fix linter error
              type: 'scatter',
              marker: {
                size: 25,
                color: HelivanColor.text.helivanBlue,
                line: { width: 2, color: 'white' },
              },
              text: ['YOU'],
              textposition: 'bottom center',
              textfont: {
                family: 'Arial, sans-serif',
                size: 14,
                color: HelivanColor.text.black,
              },
              hoverinfo: 'none',
              showlegend: false,
            },
          ]}
          layout={{
            title: { text: `Twitter Audience Visualization - ${selectedTopic.charAt(0).toUpperCase() + selectedTopic.slice(1)}` },
            autosize: true,
            height: 700,
            margin: { l: 50, r: 50, t: 80, b: 50 },
            xaxis: {
              showgrid: false,
              zeroline: false,
              showticklabels: false,
            },
            yaxis: {
              showgrid: false,
              zeroline: false,
              showticklabels: false,
            },
            hovermode: 'closest',
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            font: { color: HelivanColor.text.black },
            annotations: [
              {
                x: 0.5,
                y: 1.05,
                xref: 'paper',
                yref: 'paper',
                text: `${needsAttentionCount} followers need more attention (${attentionPercentage}%)`,
                showarrow: false,
                font: {
                  size: 14,
                  color: HelivanColor.text.gray,
                },
              },
            ],
          }}
          config={{ responsive: true }}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
    </Flex>
  );

  // Placeholder content for other tabs
  const PlaceholderContent = (tabName: string) => (
    <Flex
      height="100%"
      width="100%"
      justify="center"
      align="center"
      direction="column"
      p={8}
      overflow="auto"
    >
      <HelivanText variant="largeSubheader" mb={4}>
        {tabName} Coming Soon
      </HelivanText>
      <HelivanText variant="smallBody" textAlign="center" maxW="600px">
        This feature is currently in development. Stay tuned for updates that
        will help you better understand and engage with your audience.
      </HelivanText>
    </Flex>
  );

  return (
    <Flex
      height="full"
      width="full"
      direction="column"
      gap={0}
      overflow="hidden"
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
    >
      <Flex
        width="full"
        alignItems="center"
        justifyContent="space-between"
        py="16px"
        px="30px"
        height="fit-content"
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        position="sticky"
        flexShrink={0}
      >
        <Flex direction="column">
          <HelivanText variant="largeSubheader">Audience</HelivanText>
        </Flex>
        <Flex gap={4} alignItems="center">
          <HelivanButton variant="green" onClick={audienceModal.onOpen}>
            Create Audience
          </HelivanButton>
        </Flex>
      </Flex>

      <Flex direction="column" flex="1" overflow="hidden">
        <Tabs
          index={tabIndex}
          onChange={handleTabChange}
          variant="enclosed"
          colorScheme="blue"
          display="flex"
          flexDirection="column"
          height="100%"
          overflow="hidden"
          px={4}
          pt={4}
        >
          <TabList flexShrink={0}>
            <Tab
              _selected={{
                color: HelivanColor.text.helivanBlue,
                borderColor: HelivanColor.border.primary,
                borderBottomColor: 'transparent',
                bg: HelivanColor.background.primary,
              }}
            >
              Dashboard
            </Tab>
            <Tab
              _selected={{
                color: HelivanColor.text.helivanBlue,
                borderColor: HelivanColor.border.primary,
                borderBottomColor: 'transparent',
                bg: HelivanColor.background.primary,
              }}
            >
              Projects
            </Tab>
            <Tab
              _selected={{
                color: HelivanColor.text.helivanBlue,
                borderColor: HelivanColor.border.primary,
                borderBottomColor: 'transparent',
                bg: HelivanColor.background.primary,
              }}
            >
              Polls
            </Tab>
            <Tab
              _selected={{
                color: HelivanColor.text.helivanBlue,
                borderColor: HelivanColor.border.primary,
                borderBottomColor: 'transparent',
                bg: HelivanColor.background.primary,
              }}
            >
              Tests
            </Tab>
          </TabList>
          <TabPanels flex="1" overflow="hidden">
            <TabPanel p={0} height="100%" overflow="hidden">
              <DashboardContent />
            </TabPanel>
            <TabPanel p={0} height="100%" overflow="auto">
              {PlaceholderContent('Projects')}
            </TabPanel>
            <TabPanel p={0} height="100%" overflow="auto">
              {PlaceholderContent('Polls')}
            </TabPanel>
            <TabPanel p={0} height="100%" overflow="auto">
              {PlaceholderContent('Tests')}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>

      {/* Create Audience Modal */}
      <CreateAudienceModal
        isOpen={audienceModal.isOpen}
        onClose={audienceModal.onClose}
      />

      {/* Insights Modal */}
      <Modal
        isOpen={insightsModal.isOpen}
        onClose={insightsModal.onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent bg={HelivanColor.background.primary} maxW="800px">
          <ModalHeader>
            <Flex align="center">
              <Icon as={FaLightbulb} color="yellow.400" mr={2} />
              <HelivanText variant="largeSubheader">
                Audience Insights
              </HelivanText>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              {/* Topics followers are talking about */}
              <GridItem colSpan={1}>
                <Box mb={5}>
                  <Flex align="center" mb={2}>
                    <Icon
                      as={FaCommentDots}
                      color={HelivanColor.text.helivanBlue}
                      mr={2}
                    />
                    <HelivanText variant="subheader">
                      Trending Topics
                    </HelivanText>
                  </Flex>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                  >
                    <HelivanText variant="smallBody+">
                      Topics your followers are talking about today:
                    </HelivanText>
                    <Flex mt={2} flexWrap="wrap" gap={2}>
                      {TRENDING_TOPICS.map((topic, index) => (
                        <Tag
                          key={index}
                          size="md"
                          colorScheme="blue"
                          borderRadius="full"
                          variant="subtle"
                        >
                          <TagLabel>{topic}</TagLabel>
                        </Tag>
                      ))}
                    </Flex>
                  </Box>
                </Box>

                {/* Forgotten topics */}
                <Box mb={5}>
                  <Flex align="center" mb={2}>
                    <Icon
                      as={FaChartLine}
                      color={HelivanColor.text.gray}
                      mr={2}
                    />
                    <HelivanText variant="subheader">
                      Forgotten Topics
                    </HelivanText>
                  </Flex>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                  >
                    <HelivanText variant="smallBody+">
                      Topics your followers have forgotten about:
                    </HelivanText>
                    <Flex mt={2} flexWrap="wrap" gap={2}>
                      {FORGOTTEN_TOPICS.map((topic, index) => (
                        <Tag
                          key={index}
                          size="md"
                          colorScheme="gray"
                          borderRadius="full"
                          variant="subtle"
                        >
                          <TagLabel>{topic}</TagLabel>
                        </Tag>
                      ))}
                    </Flex>
                  </Box>
                </Box>

                {/* Content engagement */}
                <Box>
                  <Flex align="center" mb={2}>
                    <Icon as={FaChartPie} color="purple.500" mr={2} />
                    <HelivanText variant="subheader">
                      Content Engagement
                    </HelivanText>
                  </Flex>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                  >
                    <HelivanText variant="smallBody+">
                      Your content engagement this week:
                    </HelivanText>
                    <VStack align="stretch" mt={2} spacing={2}>
                      <Flex justify="space-between">
                        <HelivanText variant="smallBody">
                          Average engagement rate:
                        </HelivanText>
                        <HelivanText
                          variant="smallBody+"
                          colorOverride="green.500"
                        >
                          7.2%
                        </HelivanText>
                      </Flex>
                      <Flex justify="space-between">
                        <HelivanText variant="smallBody">
                          Total interactions:
                        </HelivanText>
                        <HelivanText variant="smallBody+">12,458</HelivanText>
                      </Flex>
                      <Flex justify="space-between">
                        <HelivanText variant="smallBody">
                          New followers:
                        </HelivanText>
                        <HelivanText
                          variant="smallBody+"
                          colorOverride="green.500"
                        >
                          +342
                        </HelivanText>
                      </Flex>
                      <Flex justify="space-between">
                        <HelivanText variant="smallBody">
                          Best performing day:
                        </HelivanText>
                        <HelivanText variant="smallBody+">
                          Wednesday
                        </HelivanText>
                      </Flex>
                    </VStack>
                  </Box>
                </Box>
              </GridItem>

              {/* Right column */}
              <GridItem colSpan={1}>
                {/* Niche engagement */}
                <Box mb={5}>
                  <HelivanText variant="subheader">
                    Niche Engagement
                  </HelivanText>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                    mt={2}
                  >
                    <Flex justify="space-between" mb={3}>
                      <HelivanText variant="smallBody+">
                        Most engaged niche:
                      </HelivanText>
                      <HelivanText
                        variant="smallBody+"
                        colorOverride="green.500"
                      >
                        {topEngagementTopic.charAt(0).toUpperCase() +
                          topEngagementTopic.slice(1)}
                      </HelivanText>
                    </Flex>
                    <Flex justify="space-between">
                      <HelivanText variant="smallBody+">
                        Least engaged niche:
                      </HelivanText>
                      <HelivanText variant="smallBody+" colorOverride="red.500">
                        {lowEngagementTopic.charAt(0).toUpperCase() +
                          lowEngagementTopic.slice(1)}
                      </HelivanText>
                    </Flex>
                  </Box>
                </Box>

                {/* Best performing post */}
                <Box mb={5}>
                  <Flex align="center" mb={2}>
                    <Icon as={FaThumbsUp} color="green.500" mr={2} />
                    <HelivanText variant="subheader">
                      Best Performing Post
                    </HelivanText>
                  </Flex>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                  >
                    <Flex justify="space-between" mb={1}>
                      <HelivanText variant="smallBody+">
                        {bestPost.title}
                      </HelivanText>
                      <HelivanText
                        variant="smallBody+"
                        colorOverride="green.500"
                      >
                        {bestPost.engagement}% engagement
                      </HelivanText>
                    </Flex>
                    <Divider my={2} />
                    <HelivanText variant="smallBody">
                      Why it performed well:
                    </HelivanText>
                    <HelivanText variant="smallBody" mt={1}>
                      {bestPost.reason}
                    </HelivanText>
                  </Box>
                </Box>

                {/* Worst performing post */}
                <Box mb={5}>
                  <Flex align="center" mb={2}>
                    <Icon as={FaThumbsDown} color="red.500" mr={2} />
                    <HelivanText variant="subheader">
                      Underperforming Post
                    </HelivanText>
                  </Flex>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                  >
                    <Flex justify="space-between" mb={1}>
                      <HelivanText variant="smallBody+">
                        {worstPost.title}
                      </HelivanText>
                      <HelivanText variant="smallBody+" colorOverride="red.500">
                        {worstPost.engagement}% engagement
                      </HelivanText>
                    </Flex>
                    <Divider my={2} />
                    <HelivanText variant="smallBody">
                      Why it underperformed:
                    </HelivanText>
                    <HelivanText variant="smallBody" mt={1}>
                      {worstPost.reason}
                    </HelivanText>
                  </Box>
                </Box>

                {/* Content suggestions */}
                <Box>
                  <Flex align="center" mb={2}>
                    <Icon as={FaLightbulb} color="yellow.400" mr={2} />
                    <HelivanText variant="subheader">
                      Content Suggestions
                    </HelivanText>
                  </Flex>
                  <Box
                    bg={HelivanColor.background.secondary}
                    p={3}
                    borderRadius="md"
                  >
                    <HelivanText variant="smallBody+">
                      Try these topics this week:
                    </HelivanText>
                    <VStack align="stretch" mt={2} spacing={2}>
                      {CONTENT_SUGGESTIONS.map((suggestion, index) => (
                        <Flex key={index} align="center">
                          <Box
                            w={1}
                            h={1}
                            bg="blue.500"
                            borderRadius="full"
                            mr={2}
                          />
                          <HelivanText variant="smallBody">
                            {suggestion}
                          </HelivanText>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              </GridItem>
            </Grid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};
